import { BatchStatus } from "~/features/batch/batch.type";
import {
  GetBatchResponse,
  GetBatchSummariesResponse,
  GetStatusCountsResponse,
  WaveProgressDto
} from "~/types/api";

import { warehouseApi } from "./warehouseApi";

export const batchesApi = warehouseApi.injectEndpoints({
  endpoints: (build) => ({
    getBatches: build.query<
      GetBatchSummariesResponse,
      {
        status: BatchStatus[];
        batchType?: string[];
        temperatureZone?: string;
        search?: string;
        limit?: number;
        offset?: number;
        startTimeStamp?: Date;
        endTimeStamp?: Date;
        isPrioritizeProcessing?: boolean; // Whether to prioritize batches that are Processing when listing batches.
        includeBatchesWithoutTaskGroup?: boolean;
        gridId?: Guid;
        toteId?: Guid;
        searchByCartIdIfToteIdProvided?: boolean;
      }
    >({
      query: ({
        status,
        batchType,
        temperatureZone,
        search,
        limit = 10,
        offset = 0,
        startTimeStamp,
        endTimeStamp,
        isPrioritizeProcessing,
        includeBatchesWithoutTaskGroup,
        gridId,
        toteId,
        searchByCartIdIfToteIdProvided
      }) => ({
        url: `/batches`,
        params: {
          status,
          batchType,
          temperatureZone,
          search,
          limit,
          offset,
          startTimeStamp,
          endTimeStamp,
          isPrioritizeProcessing,
          includeBatchesWithoutTaskGroup,
          gridId,
          toteId,
          searchByCartIdIfToteIdProvided
        }
      }),

      providesTags: ["batch"]
    }),
    getBatch: build.query<GetBatchResponse | null, { batchId: string }>({
      query: ({ batchId }) => ({
        url: `/batches/${batchId}`
      }),
      providesTags: ["batch"]
    }),
    getStatusCount: build.query<
      GetStatusCountsResponse,
      { gridId: string; portId: number | null }
    >({
      query: ({ gridId, portId }) => ({
        url: `/batches/status-counts`,
        params: {
          gridId,
          portId
        }
      }),
      providesTags: ["batch"]
    }),
    postChangeBatchWorkstation: build.mutation<
      void,
      { batchId: string; portId: number; workstationId?: Guid; gridId: Guid }
    >({
      query: ({ batchId, portId, workstationId, gridId }) => ({
        url: `/batches/${batchId}/change-workstation`,
        method: "PATCH",
        body: {
          WorkstationPortId: portId,
          WorkstationId: workstationId,
          GridId: gridId
        }
      }),
      invalidatesTags: ["batch"]
    }),
    getWaveProgress: build.query<WaveProgressDto, { batchId: string }>({
      query: ({ batchId }) => ({
        url: `/batches/${batchId}/wave-progress`
      }),
      providesTags: ["wave progress"]
    }),
    postUnsuspendBatch: build.mutation<
      void,
      { batchId: Guid; autostoreGridId?: Guid }
    >({
      query: ({ batchId, autostoreGridId }) => ({
        url: `/batches/${batchId}/unsuspend${
          autostoreGridId ? `?autostoreGridId=${autostoreGridId}` : ""
        }`,
        method: "POST"
      }),
      invalidatesTags: ["batch"]
    }),
    postForcePrepareCart: build.mutation<
      void,
      { batchId: Guid; cartNumber: string }
    >({
      query: ({ batchId, cartNumber }) => ({
        url: `/batches/${batchId}/force-prepare-cart`,
        method: "POST",
        body: {
          CartNumber: cartNumber
        }
      }),
      invalidatesTags: ["batch"]
    }),
    postAssignCart: build.mutation<void, { batchId: Guid; cartNumber: string }>(
      {
        query: ({ batchId, cartNumber }) => ({
          url: `/batches/${batchId}/carts/assign`,
          method: "POST",
          body: {
            CartNumber: cartNumber
          }
        }),
        invalidatesTags: ["batch", "tote"]
      }
    )
  }),
  overrideExisting: false
});
